/**
* Geofence Zone Report
* Rapport: hur ofta fordon befinner sig inom valda zoner (Uppsala krav 5.5.5.6)
*/
import React, { useState, useEffect, useMemo } from'react';
import { useTranslation } from'@/store/languageStore';
import { useAuth } from'@/store/authStore';
import { Link } from'react-router-dom';
import { ROUTES } from'@/constants/routes';
import {
ArrowLeftIcon,
ArrowDownTrayIcon,
MapPinIcon,
TruckIcon,
} from'@heroicons/react/24/outline';
import axios from'axios';
import {
AreaChart,
Area,
XAxis,
YAxis,
CartesianGrid,
Tooltip,
ResponsiveContainer,
} from'recharts';
import OrgTreeSelect from'@/components/common/OrgTreeSelect';
import DateInput from '@/components/common/DateInput';

interface GeofenceZoneStats {
geofenceId: string;
geofenceName: string;
vehicleId: number;
registrationNumber: string;
vehicleName: string;
organizationName: string;
visitCount: number;
totalCost: number;
totalDuration?: number;
avgDuration?: number;
lastVisit: string | null;
firstVisit: string | null;
}
interface GeofenceZoneSummary {
geofenceId: string;
geofenceName: string;
totalVehicles: number;
totalVisits: number;
totalCost: number;
totalDuration?: number;
avgDurationPerVisit?: number;
}
interface ZoneReportData {
summary: {
totalGeofences: number;
totalVehicles: number;
totalVisits: number;
totalCost: number;
totalDuration?: number;
};
zones: GeofenceZoneSummary[];
details: GeofenceZoneStats[];
}
interface GeofenceOption {
geofenceId: string;
name: string;
scope: string;
}
interface TrendData {
period: string;
visitCount: number;
vehicleCount: number;
}
const GeofenceZoneReport: React.FC = () => {
const { t } = useTranslation();
const { user } = useAuth();
// State
const [loading, setLoading] = useState(false);
const [error, setError] = useState<string | null>(null);
const [data, setData] = useState<ZoneReportData | null>(null);
const [trendData, setTrendData] = useState<TrendData[]>([]);
const [geofences, setGeofences] = useState<GeofenceOption[]>([]);
// Filters
const [startDate, setStartDate] = useState(() => {
const d = new Date();
d.setDate(d.getDate() - 30);
return d.toISOString().split('T')[0];
});
const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
const [organizationId, setOrganizationId] = useState<string>(user?.organization_id ||'');
const [selectedGeofence, setSelectedGeofence] = useState<string>('');
const [selectedZone, setSelectedZone] = useState<string | null>(null);
// Hämta tillgängliga geofences
useEffect(() => {
const fetchGeofences = async () => {
if (!organizationId) return;
try {
const response = await axios.get('/api/reports/geofence-zones/geofences', {
params: { organizationId },
});
if (response.data.success) {
setGeofences(response.data.data || []);
}
} catch {
// Error fetching geofences - handled silently
}
};
fetchGeofences();
}, [organizationId]);
// Hämta rapport
useEffect(() => {
const fetchReport = async () => {
if (!organizationId || !startDate || !endDate) return;
setLoading(true);
setError(null);
try {
const [reportRes, trendRes] = await Promise.all([
axios.get('/api/reports/geofence-zones', {
params: {
startDate,
endDate,
organizationId,
geofenceId: selectedGeofence || undefined,
},
}),
axios.get('/api/reports/geofence-zones/trend', {
params: {
startDate,
endDate,
organizationId,
geofenceId: selectedGeofence || undefined,
groupBy:'day',
},
}),
]);
if (reportRes.data.success) {
setData(reportRes.data.data);
}
if (trendRes.data.success) {
setTrendData(trendRes.data.data || []);
}
} catch (err: any) {
setError(err.response?.data?.error || t('geofenceZone.fetchError'));
} finally {
setLoading(false);
}
};
fetchReport();
}, [organizationId, startDate, endDate, selectedGeofence]);
// Filtrera detaljer per vald zon
const filteredDetails = useMemo(() => {
if (!data?.details) return [];
if (!selectedZone) return data.details;
return data.details.filter((d) => d.geofenceId === selectedZone);
}, [data, selectedZone]);
// Export
const handleExport = async () => {
try {
const response = await axios.get('/api/reports/geofence-zones/export', {
params: {
startDate,
endDate,
organizationId,
geofenceId: selectedGeofence || undefined,
format:'csv',
},
responseType:'blob',
});
const url = window.URL.createObjectURL(new Blob([response.data]));
const link = document.createElement('a');
link.href = url;
link.setAttribute('download', `geofence_zone_report_${startDate}_${endDate}.csv`);
document.body.appendChild(link);
link.click();
link.remove();
} catch {
// Error exporting - handled silently
}
};
return (
<div className="min-h-screen bg-white dark:bg-bgContainer p-6">
{/* Header */}
<div className="flex items-center justify-between mb-6">
<div className="flex items-center gap-4">
<Link
to={ROUTES.REPORTS}
className="p-2 rounded-lg hover:bg-bgElevated transition-colors"
>
<ArrowLeftIcon className="h-5 w-5 text-textSecondary"/>
</Link>
<div>
<h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
<MapPinIcon className="w-6 h-6 text-primary"/>
{t('geofenceZone.title') ||'Geofence Zonrapport'}
</h1>
<p className="text-textSecondary">
{t('geofenceZone.description') ||'Analysera hur ofta fordon befinner sig inom valda zoner'}
</p>
</div>
</div>
<button
onClick={handleExport}
className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
>
<ArrowDownTrayIcon className="h-5 w-5"/>
{t('common.export') ||'Exportera'}
</button>
</div>
{/* Filters */}
<div className="bg-bgElevated rounded-xl shadow-sm border border-border p-4 mb-6">
<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
<div>
<label className="block text-sm font-medium text-textSecondary mb-1">
{t('common.organization')}
</label>
<OrgTreeSelect
value={organizationId}
onChange={setOrganizationId}
showEmptyOption={false}
/>
</div>
<div>
<label className="block text-sm font-medium text-textSecondary mb-1">
{t('common.startDate') ||'Startdatum'}
</label>
<DateInput value={startDate}
onChange={(e) => setStartDate(e.target.value)}
className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary"
/>
</div>
<div>
<label className="block text-sm font-medium text-textSecondary mb-1">
{t('common.endDate') ||'Slutdatum'}
</label>
<DateInput value={endDate}
onChange={(e) => setEndDate(e.target.value)}
className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary"
/>
</div>
<div>
<label className="block text-sm font-medium text-textSecondary mb-1">
{t('geofenceZone.selectZone')}
</label>
<select
value={selectedGeofence}
onChange={(e) => setSelectedGeofence(e.target.value)}
className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary"
>
<option value="">{t('common.all') ||'Alla'}</option>
{geofences.map((g) => (
<option key={g.geofenceId} value={g.geofenceId}>
{g.name} {g.scope ==='global' ? `(${t('geofenceZone.globalLabel')})` :''}
</option>
))}
</select>
</div>
</div>
</div>
{/* Loading */}
{loading && (
<div className="flex justify-center items-center py-12">
<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
</div>
)}
{/* Error */}
{error && (
<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
{error}
</div>
)}
{/* Content */}
{!loading && data && (
<>
{/* Summary Cards */}
<div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
<div className="bg-bgElevated rounded-xl shadow-sm border border-border p-4">
<div className="flex items-center gap-3">
<div className="p-2 bg-blue-100 rounded-lg">
<MapPinIcon className="h-6 w-6 text-blue-600"/>
</div>
<div>
<p className="text-sm text-textSecondary">{t('geofenceZone.totalZones')}</p>
<p className="text-2xl font-bold text-textPrimary">{data.summary.totalGeofences}</p>
</div>
</div>
</div>
<div className="bg-bgElevated rounded-xl shadow-sm border border-border p-4">
<div className="flex items-center gap-3">
<div className="p-2 bg-green-100 rounded-lg">
<TruckIcon className="h-6 w-6 text-green-600"/>
</div>
<div>
<p className="text-sm text-textSecondary">{t('geofenceZone.totalVehicles')}</p>
<p className="text-2xl font-bold text-textPrimary">{data.summary.totalVehicles}</p>
</div>
</div>
</div>
<div className="bg-bgElevated rounded-xl shadow-sm border border-border p-4">
<div className="flex items-center gap-3">
<div className="p-2 bg-purple-100 rounded-lg">
<MapPinIcon className="h-6 w-6 text-purple-600"/>
</div>
<div>
<p className="text-sm text-textSecondary">{t('geofenceZone.totalVisits')}</p>
<p className="text-2xl font-bold text-textPrimary">
{data.summary.totalVisits.toLocaleString('sv-SE')}
</p>
</div>
</div>
</div>
<div className="bg-bgElevated rounded-xl shadow-sm border border-border p-4">
<div className="flex items-center gap-3">
<div className="p-2 bg-amber-100 rounded-lg">
<span className="text-2xl">💰</span>
</div>
<div>
<p className="text-sm text-textSecondary">{t('geofenceZone.totalCost')}</p>
<p className="text-2xl font-bold text-textPrimary">
{(data.summary.totalCost ?? 0).toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} kr
</p>
</div>
</div>
</div>
</div>
{/* Trend Chart */}
{trendData.length > 0 && (
<div className="bg-bgElevated rounded-xl shadow-sm border border-border p-4 mb-6">
<h2 className="text-lg font-semibold text-textPrimary mb-4">
{t('geofenceZone.visitsOverTime')}
</h2>
<div className="h-64">
<ResponsiveContainer width="100%"height="100%">
<AreaChart data={trendData}>
<defs>
<linearGradient id="colorVisits"x1="0"y1="0"x2="0"y2="1">
<stop offset="5%"stopColor="#6366f1"stopOpacity={0.3} />
<stop offset="95%"stopColor="#6366f1"stopOpacity={0} />
</linearGradient>
</defs>
<CartesianGrid strokeDasharray="3 3"stroke="#e5e7eb"/>
<XAxis dataKey="period"fontSize={12} tickFormatter={(v) => v.slice(-5)} />
<YAxis fontSize={12} />
<Tooltip
formatter={(value: number, name: string) => [
value,
name ==='visitCount' ? t('geofenceZone.visits') : t('geofenceZone.vehicles'),
]}
/>
<Area
type="monotone"
dataKey="visitCount"
stroke="#6366f1"
fill="url(#colorVisits)"
strokeWidth={2}
/>
</AreaChart>
</ResponsiveContainer>
</div>
</div>
)}
{/* Zone Summary */}
{data.zones.length > 0 && (
<div className="bg-bgElevated rounded-xl shadow-sm border border-border p-4 mb-6">
<h2 className="text-lg font-semibold text-textPrimary mb-4">
{t('geofenceZone.zoneSummary')}
</h2>
<div className="overflow-x-auto">
<table className="min-w-full">
<thead>
<tr className="border-b border-border">
<th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">
{t('geofenceZone.zone') ||'Zon'}
</th>
<th className="px-4 py-3 text-right text-xs font-medium text-textSecondary uppercase">
{t('geofenceZone.vehicles') ||'Fordon'}
</th>
<th className="px-4 py-3 text-right text-xs font-medium text-textSecondary uppercase">
{t('geofenceZone.visits') ||'Besök'}
</th>
<th className="px-4 py-3 text-right text-xs font-medium text-textSecondary uppercase">
{t('geofenceZone.costColumn') ||'Kostnad'}
</th>
</tr>
</thead>
<tbody>
{data.zones.map((zone) => (
<tr
key={zone.geofenceId}
onClick={() => setSelectedZone(selectedZone === zone.geofenceId ? null : zone.geofenceId)}
className={`border-b border-border hover:bg-bgBase/50 cursor-pointer transition-colors ${
selectedZone === zone.geofenceId ?'bg-primary/10' :''
}`}
>
<td className="px-4 py-3 text-sm text-textPrimary font-medium">
<div className="flex items-center gap-2">
<MapPinIcon className="h-4 w-4 text-primary"/>
{zone.geofenceName}
</div>
</td>
<td className="px-4 py-3 text-sm text-textPrimary text-right">
{zone.totalVehicles}
</td>
<td className="px-4 py-3 text-sm text-textPrimary text-right">
{zone.totalVisits.toLocaleString('sv-SE')}
</td>
<td className="px-4 py-3 text-sm text-textPrimary text-right">
{(zone.totalCost ?? 0).toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} kr
</td>
</tr>
))}
</tbody>
</table>
</div>
</div>
)}
{/* Vehicle Details */}
<div className="bg-bgElevated rounded-xl shadow-sm border border-border p-4">
<h2 className="text-lg font-semibold text-textPrimary mb-4">
{t('geofenceZone.vehicleDetails')}
{selectedZone && (
<span className="ml-2 text-sm font-normal text-primary">
({t('geofenceZone.filteredLabel')}: {data.zones.find((z) => z.geofenceId === selectedZone)?.geofenceName})
<button
onClick={() => setSelectedZone(null)}
className="ml-2 text-textSecondary hover:text-textPrimary"
>
✕
</button>
</span>
)}
</h2>
{filteredDetails.length === 0 ? (
<p className="text-textSecondary py-8 text-center">
{t('geofenceZone.noData')}
</p>
) : (
<div className="overflow-x-auto">
<table className="min-w-full">
<thead>
<tr className="border-b border-border">
<th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">
{t('geofenceZone.zone') ||'Zon'}
</th>
<th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">
{t('common.vehicle') ||'Fordon'}
</th>
<th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">
{t('common.organization') ||'Organisation'}
</th>
<th className="px-4 py-3 text-right text-xs font-medium text-textSecondary uppercase">
{t('geofenceZone.visits') ||'Besök'}
</th>
<th className="px-4 py-3 text-right text-xs font-medium text-textSecondary uppercase">
{t('geofenceZone.costColumn') ||'Kostnad'}
</th>
<th className="px-4 py-3 text-right text-xs font-medium text-textSecondary uppercase">
{t('geofenceZone.lastVisit') ||'Senaste'}
</th>
</tr>
</thead>
<tbody>
{filteredDetails.slice(0, 100).map((detail, idx) => (
<tr key={`${detail.geofenceId}-${detail.vehicleId}-${idx}`} className="border-b border-border hover:bg-bgBase/50">
<td className="px-4 py-3 text-sm text-textPrimary">
{detail.geofenceName}
</td>
<td className="px-4 py-3 text-sm text-textPrimary font-medium">
{detail.registrationNumber}
</td>
<td className="px-4 py-3 text-sm text-textSecondary">
{detail.organizationName}
</td>
<td className="px-4 py-3 text-sm text-textPrimary text-right">
{detail.visitCount}
</td>
<td className="px-4 py-3 text-sm text-textPrimary text-right">
{(detail.totalCost ?? 0).toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} kr
</td>
<td className="px-4 py-3 text-sm text-textSecondary text-right">
{detail.lastVisit
? new Date(detail.lastVisit).toLocaleDateString('sv-SE')
:'-'}
</td>
</tr>
))}
</tbody>
</table>
{filteredDetails.length > 100 && (
<p className="text-sm text-textSecondary text-center py-2">
{t('geofenceZone.showingRowsCount', { count: 100, total: filteredDetails.length })}
</p>
)}
</div>
)}
</div>
</>
)}
</div>
);
};
export default GeofenceZoneReport;
